import React, { ElementType, ComponentPropsWithRef, forwardRef } from 'react';
import { css, cva, cx } from '@mildang/styled-system/css';
import { SystemStyleObject } from '@mildang/styled-system/types';

const iconButtonStyle = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    boxSizing: 'border-box',
    WebkitTapHighlightColor: 'transparent',
    backgroundColor: 'transparent', // Reset default value
    outline: 0, // We disable the focus ring for mouse, touch and keyboard users.
    border: 0,
    borderRadius: 'md',
    margin: '0', // Remove the margin in Safari
    userSelect: 'none',
    verticalAlign: 'middle',
    appearance: 'none', // Reset
    WebkitAppearance: 'none', // Reset
    textDecoration: 'none',
    '&::-moz-focus-inner': {
      borderStyle: 'none', // Remove Firefox dotted outline.
    },
    '@media print': {
      printColorAdjust: 'exact',
    },
    _hover: {
      backgroundColor: 'common.a1',
      cursor: 'pointer',
    },
  },
  variants: {
    disabled: {
      true: {
        opacity: '0.38',
        backgroundColor: 'transparent',
        pointerEvents: 'none', // Disable link interactions
        cursor: 'not-allowed',
      },
    },
    color: {
      default: {
        color: 'common.mediumBg.normal',
        _hover: {
          backgroundColor: 'common.a1',
        },
        _active: {
          backgroundColor: 'common.a2',
        },
      },
      inherit: {
        color: 'currentColor',
        _hover: {
          backgroundColor: 'currentColor',
        },
        _active: {
          backgroundColor: 'currentColor',
        },
      },
      primary: {
        color: 'common.mediumBg.normal',
        _hover: {
          backgroundColor: 'common.a1',
        },
        _active: {
          backgroundColor: 'common.a2',
        },
      },
      danger: {
        color: 'error.7',
        _hover: {
          backgroundColor: 'error.a1',
        },
        _active: {
          backgroundColor: 'error.a2',
        },
      },
      error: {
        color: 'error.7',
        _hover: {
          backgroundColor: 'error.3',
        },
        _active: {
          backgroundColor: 'error.a2',
        },
      },
      success: {
        color: 'success.7',
        _hover: {
          backgroundColor: 'success.a1',
        },
        _active: {
          backgroundColor: 'success.a2',
        },
      },
      white: {
        color: 'gray.light.100',
        _hover: {
          backgroundColor: 'common.a1',
        },
        _active: {
          backgroundColor: 'common.a2',
        },
      },
    },
    size: {
      xSmall: {
        width: '24px',
        height: '24px',
        padding: '3px',
        '& svg': {
          fontSize: '18px',
        },
      },
      small: {
        width: '32px',
        height: '32px',
        padding: '5px',
        '& svg': {
          fontSize: '22px',
        },
      },
      medium: {
        width: '36px',
        height: '36px',
        padding: '6px',
        '& svg': {
          fontSize: '24px',
        },
      },
      large: {
        width: '42px',
        height: '42px',
        padding: '7px',
        '& svg': {
          fontSize: '28px',
        },
      },
      xLarge: {
        width: '50px',
        height: '50px',
        padding: '9px',
        '& svg': {
          fontSize: '32px',
        },
      },
    },
    outlined: {
      true: {
        border: '1px solid',
        borderColor: 'common.5',
      },
    },
    selected: {
      true: {
        backgroundColor: 'success.a2',
        color: 'success.7',
        _hover: {
          backgroundColor: 'success.a1',
        },
        _active: {
          backgroundColor: 'success.a2',
        },
      },
    },
  },
  compoundVariants: [
    {
      color: 'default',
      outlined: true,
      css: {
        borderColor: 'common.5',
        _active: {
          borderColor: 'common.6',
        },
      },
    },
    {
      color: 'primary',
      outlined: true,
      css: {
        borderColor: 'common.5',
        _active: {
          borderColor: 'common.6',
        },
      },
    },
    {
      color: 'success',
      outlined: true,
      css: {
        borderColor: 'success.5',
        _active: {
          borderColor: 'success.7',
        },
      },
    },
    {
      color: 'error',
      outlined: true,
      css: {
        borderColor: 'error.5',
        _active: {
          borderColor: 'error.7',
        },
      },
    },
    {
      color: 'white',
      outlined: true,
      css: {
        borderColor: 'common.5',
        _active: {
          borderColor: 'common.6',
        },
      },
    },
    ///---
    {
      color: 'default',
      selected: true,
      css: {
        borderColor: 'success.7',
        _active: {
          borderColor: 'success.7',
        },
      },
    },
    {
      color: 'primary',
      outlined: true,
      selected: true,
      css: {
        borderColor: 'success.7',
        _active: {
          borderColor: 'success.7',
        },
      },
    },
    {
      color: 'success',
      outlined: true,
      selected: true,
      css: {
        borderColor: 'success.7',
        _active: {
          borderColor: 'success.7',
        },
      },
    },
    {
      color: 'error',
      outlined: true,
      selected: true,
      css: {
        color: 'error.7',
        backgroundColor: 'error.a2',
        borderColor: 'error.7',
        _active: {
          borderColor: 'error.7',
        },
      },
    },
    {
      color: 'white',
      outlined: true,
      selected: true,
      css: {
        borderColor: 'success.7',
        _active: {
          borderColor: 'success.7',
        },
      },
    },
    //---
    {
      color: 'error',
      selected: true,
      css: {
        backgroundColor: 'error.a2',
        color: 'error.7',
        _hover: {
          backgroundColor: 'error.a1',
        },
        _active: {
          backgroundColor: 'error.a2',
        },
      },
    },
  ],
});

export type IconButtonSize = 'xSmall' | 'small' | 'medium' | 'large' | 'xLarge';
export type IconButtonColor = 'default' | 'primary' | 'success' | 'white' | 'error' | 'inherit';

export interface IconButtonPropsGeneric<T extends ElementType>
  extends React.HTMLAttributes<HTMLButtonElement> {
  component?: T;
  children?: React.ReactNode;
  /**
   * @default 'default'
   */
  color?: IconButtonColor;
  /**
   * @default false
   */
  disabled?: boolean;
  /**
   * @default false
   */
  outlined?: boolean;
  /**
   * @default false
   */
  selected?: boolean;
  /**
   * @default 'medium'
   */
  size?: IconButtonSize;
  panda?: SystemStyleObject;
  className?: string;
}

export type IconButtonProps<T extends ElementType = 'button'> = IconButtonPropsGeneric<T> &
  ComponentPropsWithRef<T>;

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps<any>>(
  <T extends ElementType = 'button'>(
    { children, component: Component = 'button' as T, ...props }: IconButtonProps<T>,
    ref: React.Ref<HTMLButtonElement>, // Explicitly type the ref parameter
  ) => {
    const {
      disabled = false,
      color = 'default',
      size = 'medium',
      outlined = false,
      selected = false,
      panda,
      className,
    } = props;

    return (
      <Component
        {...props}
        ref={ref}
        className={cx(iconButtonStyle({ disabled, color, size, outlined, selected }), css(panda), className)}
      >
        {children}
      </Component>
    );
  },
);

export default IconButton;
